import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { MCPServer } from '@/api/mcpServer'

interface MCPServerFormProps {
  initialData?: MCPServer
  onSubmit: (data: MCPServerFormData) => Promise<void>
  onCancel: () => void
  submitLabel?: string
}

export interface MCPServerFormData {
  name: string
  description: string
  command: string
  args: string
  env_vars: string
  enabled: boolean
}

export function MCPServerForm({
  initialData,
  onSubmit,
  onCancel,
  submitLabel = 'Save',
}: MCPServerFormProps) {
  const [formData, setFormData] = useState<MCPServerFormData>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    command: initialData?.command || 'node',
    args: initialData?.args ? JSON.stringify(initialData.args, null, 2) : '[]',
    env_vars: initialData?.env_vars
      ? JSON.stringify(initialData.env_vars, null, 2)
      : '{}',
    enabled: initialData?.enabled ?? true,
  })

  const [errors, setErrors] = useState<Partial<MCPServerFormData>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const validateJSON = (value: string, fieldName: string): boolean => {
    try {
      JSON.parse(value)
      setErrors((prev) => ({ ...prev, [fieldName]: undefined }))
      return true
    } catch (e) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: 'Invalid JSON format',
      }))
      return false
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate required fields
    const newErrors: Partial<MCPServerFormData> = {}
    if (!formData.name.trim()) {
      newErrors.name = 'Server name is required'
    }
    if (!formData.command.trim()) {
      newErrors.command = 'Command is required'
    }

    // Validate JSON fields
    const argsValid = validateJSON(formData.args, 'args')
    const envVarsValid = validateJSON(formData.env_vars, 'env_vars')

    if (Object.keys(newErrors).length > 0 || !argsValid || !envVarsValid) {
      setErrors(newErrors)
      return
    }

    setIsSubmitting(true)
    try {
      await onSubmit(formData)
    } catch (error) {
      console.error('Form submission error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className='space-y-4'>
      <div className='space-y-2'>
        <Label htmlFor='name'>
          Server Name <span className='text-red-500'>*</span>
        </Label>
        <Input
          id='name'
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder='e.g., Nuclei Scanner'
          className={errors.name ? 'border-red-500' : ''}
        />
        {errors.name && <p className='text-sm text-red-500'>{errors.name}</p>}
      </div>

      <div className='space-y-2'>
        <Label htmlFor='description'>Description</Label>
        <Textarea
          id='description'
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
          placeholder='What does this MCP server do?'
          rows={2}
        />
      </div>

      <div className='space-y-2'>
        <Label htmlFor='command'>
          Command <span className='text-red-500'>*</span>
        </Label>
        <Input
          id='command'
          value={formData.command}
          onChange={(e) =>
            setFormData({ ...formData, command: e.target.value })
          }
          placeholder='e.g., node, python'
          className={errors.command ? 'border-red-500' : ''}
        />
        {errors.command && (
          <p className='text-sm text-red-500'>{errors.command}</p>
        )}
      </div>

      <div className='space-y-2'>
        <Label htmlFor='args'>
          Arguments (JSON Array) <span className='text-red-500'>*</span>
        </Label>
        <Textarea
          id='args'
          value={formData.args}
          onChange={(e) => {
            setFormData({ ...formData, args: e.target.value })
            validateJSON(e.target.value, 'args')
          }}
          placeholder='["/path/to/script.js", "param1"]'
          rows={3}
          className={`font-mono text-sm ${errors.args ? 'border-red-500' : ''}`}
        />
        {errors.args && <p className='text-sm text-red-500'>{errors.args}</p>}
        <p className='text-xs text-muted-foreground'>
          Provide command arguments as a JSON array
        </p>
      </div>

      <div className='space-y-2'>
        <Label htmlFor='env_vars'>Environment Variables (JSON Object)</Label>
        <Textarea
          id='env_vars'
          value={formData.env_vars}
          onChange={(e) => {
            setFormData({ ...formData, env_vars: e.target.value })
            validateJSON(e.target.value, 'env_vars')
          }}
          placeholder='{"API_KEY": "your-key-here"}'
          rows={3}
          className={`font-mono text-sm ${errors.env_vars ? 'border-red-500' : ''}`}
        />
        {errors.env_vars && (
          <p className='text-sm text-red-500'>{errors.env_vars}</p>
        )}
        <p className='text-xs text-muted-foreground'>
          Optional environment variables as a JSON object
        </p>
      </div>

      <div className='flex items-center space-x-2'>
        <Switch
          id='enabled'
          checked={formData.enabled}
          onCheckedChange={(checked) =>
            setFormData({ ...formData, enabled: checked })
          }
        />
        <Label htmlFor='enabled'>Enable this server</Label>
      </div>

      <div className='flex gap-2 justify-end pt-4'>
        <Button
          type='button'
          variant='outline'
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type='submit'
          disabled={isSubmitting}
          className='bg-dark-blue hover:bg-dark-blue-hovered'
        >
          {isSubmitting ? 'Saving...' : submitLabel}
        </Button>
      </div>
    </form>
  )
}
